import { Injectable, NgZone } from '@angular/core';
import { UUID } from 'angular2-uuid';
import { BehaviorSubject } from 'rxjs';
import { TerminalDataService } from '../private/terminal/services/terminal-data.service';
import { TerminalAuthService } from '../services/terminal-auth.service';
import { DeviceWsConnector } from './device-ws.connector';
import { PosCommandEnvelope } from './pos-command.models';
import { PosTerminalSupportService } from './pos-terminal-support.service';

export interface SupportChatMessage {
  id: string;
  from: 'terminal' | 'admin';
  author?: string;
  text: string;
  // request: the support request that started the chat.
  kind?: 'message' | 'request';
  atUtc: string;
  // Terminal messages: sending until Pos.Ws took it, then sent or failed.
  status?: 'sending' | 'sent' | 'failed' | 'delivered';
  error?: string;
}

export interface SupportChatState {
  // none: no chat yet; closed: the admin closed it (the window closes, the history stays).
  status: 'none' | 'open' | 'closed';
  chatId?: string;
  operator?: string;
  messages: SupportChatMessage[];
  minimized: boolean;
  // Admin messages that arrived while the chat was minimized.
  unread: number;
  hidden?: boolean;
}

// The "dialog" command's chat payload (sent by Erp.Ws SetSupportChatState / SendSupportChatMessage).
export interface ChatDialogPayload {
  type: 'chat';
  action: 'open' | 'close' | 'message';
  chatId?: string;
  operator?: string;
  messages?: SupportChatMessage[];
  message?: SupportChatMessage;
}

const STATE_KEY = 'pos_support_chat';
const MAX_MESSAGES = 200;
export const CHAT_MESSAGE_MAX = 2000;

// The support chat on the terminal: the admin opens and closes it, both sides write. Admin messages
// arrive as "dialog" commands; the user's go to Pos.Ws (SendChatMessage) and from there to the admins.
// SupportChatComponent shows it without an overlay, over every window.
@Injectable({ providedIn: 'root' })
export class PosTerminalChatService {
  private readonly _state = new BehaviorSubject<SupportChatState>(this._read());
  readonly state$ = this._state.asObservable();

  constructor(
    private _connector: DeviceWsConnector,
    private _terminalAuth: TerminalAuthService,
    private _terminalData: TerminalDataService,
    private _zone: NgZone,
    private _support: PosTerminalSupportService,
  ) {}

  get state(): SupportChatState {
    return this._state.value;
  }

  // The dialog command's chat part; returns what the admin sees as the command result.
  apply(payload: ChatDialogPayload, envelope: PosCommandEnvelope) {
    return this._zone.run(() => {
      const operator = payload.operator || envelope?.issuedBy || this.state.operator;
      const current = this.state;

      if (payload.action === 'open') {
        // The server's history, plus the user's messages it does not have yet.
        const history = (payload.messages || []).map((m) => this._normalize(m));
        const ids = new Set(history.map((m) => m.id));
        const unsent = current.messages.filter((m) => m.from === 'terminal' && m.status !== 'sent' && !ids.has(m.id));
        this._set({ status: 'open', chatId: payload.chatId, operator, messages: [...history, ...unsent], minimized: false, unread: 0 });
      } else if (payload.action === 'close') {
        // The admin ended the chat: its window closes (history kept for the next open).
        this._set({ ...current, status: 'closed', operator, minimized: false, unread: 0, hidden: true });
        // The chat is the support session: closing it ends the "support connected" badge too.
        this._support.endSession();
      } else if (payload.action === 'message') {
        if (!payload.message?.id || !payload.message.text) {
          throw new Error('Chat message has no id or text');
        }

        const message = this._normalize(payload.message);
        if (!current.messages.some((m) => m.id === message.id)) {
          // An admin message also opens the chat if this terminal missed the open command.
          this._set({
            ...current,
            status: 'open',
            hidden: false,
            chatId: payload.chatId || current.chatId,
            operator,
            messages: [...current.messages, message],
            unread: current.minimized ? current.unread + 1 : 0,
          });
        }
      } else {
        throw new Error(`Unknown chat action "${(payload as { action?: string }).action}"`);
      }

      return { status: this.state.status, messages: this.state.messages.length, minimized: this.state.minimized };
    });
  }

  async send(text: string): Promise<void> {
    const value = (text || '').trim().slice(0, CHAT_MESSAGE_MAX);
    if (!value || this.state.status !== 'open') {
      return;
    }

    const message: SupportChatMessage = {
      id: UUID.UUID(),
      from: 'terminal',
      author: this._employeeName(),
      text: value,
      atUtc: new Date().toISOString(),
      status: 'sending',
    };
    this._set({ ...this.state, messages: [...this.state.messages, message] });
    await this._deliver(message);
  }

  retry(messageId: string): Promise<void> {
    const message = this.state.messages.find((m) => m.id === messageId && m.status === 'failed');
    if (!message) {
      return Promise.resolve();
    }

    this._update(message.id, { status: 'sending', error: undefined });
    return this._deliver(message);
  }

  minimize(): void {
    this._set({ ...this.state, minimized: true });
  }

  expand(): void {
    this._set({ ...this.state, minimized: false, hidden: false, unread: 0 });
  }

  // Hides a closed chat; the next open command shows it again.
  dismiss(): void {
    if (this.state.status !== 'open') {
      this._set({ ...this.state, hidden: true });
    }
  }

  private async _deliver(message: SupportChatMessage): Promise<void> {
    try {
      await this._connector.invokeOrFail('SendChatMessage', {
        chatId: this.state.chatId,
        messageId: message.id,
        text: message.text,
        employeeName: message.author,
        storeName: this._storeName(),
      });
      this._zone.run(() => this._update(message.id, { status: 'sent' }));
    } catch (err) {
      this._zone.run(() => this._update(message.id, { status: 'failed', error: (err as Error)?.message || String(err) }));
    }
  }

  private _update(id: string, change: Partial<SupportChatMessage>): void {
    this._set({ ...this.state, messages: this.state.messages.map((m) => (m.id === id ? { ...m, ...change } : m)) });
  }

  private _normalize(message: SupportChatMessage): SupportChatMessage {
    return {
      id: String(message.id),
      from: message.from === 'admin' ? 'admin' : 'terminal',
      author: message.author || undefined,
      text: String(message.text ?? ''),
      kind: message.kind === 'request' ? 'request' : 'message',
      atUtc: message.atUtc || new Date().toISOString(),
      status: message.from === 'admin' ? undefined : 'sent',
    };
  }

  private _set(state: SupportChatState): void {
    const next = { ...state, messages: state.messages.slice(-MAX_MESSAGES) };
    this._state.next(next);
    try {
      if (next.status === 'none') {
        localStorage.removeItem(STATE_KEY);
      } else {
        localStorage.setItem(STATE_KEY, JSON.stringify(next));
      }
    } catch {
      // the chat is empty after a reload without storage
    }
  }

  private _read(): SupportChatState {
    const empty: SupportChatState = { status: 'none', messages: [], minimized: false, unread: 0 };
    try {
      const stored = JSON.parse(localStorage.getItem(STATE_KEY) || 'null');
      if (!stored || !Array.isArray(stored.messages)) {
        return empty;
      }

      // A message that was being sent when the app closed did not reach Pos.Ws.
      const messages = stored.messages.map((m: SupportChatMessage) =>
        m.status === 'sending' ? { ...m, status: 'failed', error: 'not sent' } : m,
      );
      return { ...empty, ...stored, messages };
    } catch {
      return empty;
    }
  }

  private _employeeName(): string | undefined {
    try {
      return this._terminalAuth.currentUser?.full_name || undefined;
    } catch {
      return undefined;
    }
  }

  private _storeName(): string | undefined {
    try {
      const store = this._terminalData.terminalSettings()?.store;
      return store?.name || store?.erpName || undefined;
    } catch {
      return undefined;
    }
  }
}
